/**
 * Crea un Task commerciale da un suggerimento AI della chat, collegato al
 * Lead/Cliente della conversazione. Se la chat ha anche un progetto, si puo'
 * scegliere di creare invece un task del progetto (modale gia' esistente).
 */
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ListTodo, Loader2, FolderKanban } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useApp } from '@/components/AppProvider';
import { useWhatsAppAgentStore } from '@/lib/stores/whatsappAgentStore';
import { defaultDueDate, suggestionDescription, type SalesTaskSource } from './salesTaskFromSuggestion';
import { createSalesTasks } from './salesTaskService';
import { iconTip } from '@/components/messages/ChatTooltip';

interface Props {
  /** Suggerimento WhatsApp (con id) o risposta dell'assistente su Instagram/Messenger (senza id). */
  request: SalesTaskSource;
  clientId: string | null;
  clientName: string | null;
  organizationId: string | null;
  projectName: string | null;
  onClose: () => void;
  /** Presente solo se la chat ha un progetto: apre la creazione del task di progetto. */
  onChooseProject?: () => void;
  onCreated?: () => void;
}

/** yyyy-MM-ddTHH:mm in ora locale, per <input type="datetime-local">. */
function toLocalInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function SalesTaskFromSuggestionModal({
  request, clientId, clientName, organizationId, projectName, onClose, onChooseProject, onCreated,
}: Props) {
  const { user } = useAuth();
  const { toast } = useApp();
  const [title, setTitle] = useState(request.title);
  const [description, setDescription] = useState(() => suggestionDescription(request));
  const [due, setDue] = useState(() => toLocalInput(defaultDueDate(new Date())));
  const [saving, setSaving] = useState(false);

  const canCreate = !!clientId && !!organizationId && !!user?.id && title.trim().length > 0;
  const dueDate = useMemo(() => (due ? new Date(due) : null), [due]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleCreate = async () => {
    if (!canCreate || !dueDate || Number.isNaN(dueDate.getTime())) return;
    setSaving(true);
    try {
      const res = await createSalesTasks([{
        request,
        input: { title, description, dueDate, clientId: clientId!, userId: user!.id, organizationId: organizationId! },
      }]);
      if (res.created > 0) {
        if (request.id) useWhatsAppAgentStore.setState((s) => ({ chatSuggestions: s.chatSuggestions.filter((x) => x.id !== request.id) }));
        toast.success('Task commerciale creato');
        onCreated?.();
        onClose();
      } else {
        toast.error(res.errors[0] || 'Non sono riuscito a creare il Task commerciale');
      }
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 outline-none focus:border-[#2383E2] text-gray-900 dark:text-white';

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="sales-task-title"
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <ListTodo className="w-5 h-5 text-[#2383E2]" />
            <h3 id="sales-task-title" className="font-semibold text-gray-900 dark:text-white">Nuovo Task commerciale</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700" {...iconTip('Chiudi')}>
            <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3 overflow-y-auto">
          {clientId ? (
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Il Task commerciale viene aggiunto alla scheda di <strong>{clientName || 'questo Lead/Cliente'}</strong>.
            </p>
          ) : (
            <p className="text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-2">
              Per creare un Task commerciale collega prima questa conversazione a un Lead o Cliente con il pulsante
              "Collega Lead/Cliente" in alto.
            </p>
          )}
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Titolo</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Descrizione</span>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={5} className={`${inputCls} resize-y`} />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Scadenza</span>
            <input type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} className={inputCls} />
          </label>
          {onChooseProject && (
            <button
              type="button"
              onClick={onChooseProject}
              className="flex items-center gap-1.5 text-xs font-medium text-[#2383E2] hover:underline"
            >
              <FolderKanban className="w-3.5 h-3.5" />
              Crea invece un task nel progetto {projectName ? `"${projectName}"` : 'collegato'}
            </button>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            Annulla
          </button>
          <button
            onClick={handleCreate}
            disabled={!canCreate || saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#2383E2] hover:bg-[#1a6fc2] disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Crea Task commerciale
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
